// Web Push helper for J & W Deli.
//
// Idempotent: calling subscribe() multiple times for the same context will
// just upsert the subscription server-side. We never throw — push is a
// nice-to-have and must never block the UX.

import { api } from "@/lib/api";

function urlBase64ToUint8Array(b64) {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
}

export function pushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

/**
 * Subscribes the current device to push notifications for an order or a
 * driver user. Returns true on success, false otherwise (no permission, no
 * support, network error). Safe to call repeatedly — upserts server-side.
 *
 * @param {{ orderId?: string, driverUserId?: string }} ctx
 */
export async function subscribePush(ctx = {}) {
    if (!pushSupported()) return false;
    if (Notification.permission === "denied") return false;
    try {
        // Ask once. If the user dismisses, we accept that decision quietly.
        if (Notification.permission === "default") {
            const p = await Notification.requestPermission();
            if (p !== "granted") return false;
        }

        const reg = await navigator.serviceWorker.ready;
        const keyRes = await api.get("/push/public-key");
        const publicKey = keyRes.data?.public_key;
        if (!publicKey) return false;

        // Reuse existing subscription if any (browsers cache it forever).
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });
        }
        const raw = sub.toJSON();
        await api.post("/push/subscribe", {
            endpoint: raw.endpoint,
            keys: raw.keys,
            order_id: ctx.orderId || null,
            driver_user_id: ctx.driverUserId || null,
        });
        return true;
    } catch {
        return false;
    }
}

export async function unsubscribePush() {
    if (!pushSupported()) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            await api.post("/push/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys });
            await sub.unsubscribe();
        }
    } catch { /* noop */ }
}
